import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { format, isSameDay } from "date-fns";
import { de } from "date-fns/locale";
import { Clock, MapPin } from "lucide-react";


interface FollowUp {
  id: string;
  next_reminder: string;
  unit_name?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  d2d_houses: {
    id: string;
    house_number: string;
    d2d_streets: {
      id: string;
      name: string;
      d2d_areas: {
        id: string;
        name: string;
      };
    };
  };
}

interface D2DFollowUpCalendarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

export const D2DFollowUpCalendar = ({ open, onOpenChange, userId }: D2DFollowUpCalendarProps) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      updateOverdueFollowUps();
    }
  }, [open, userId]);

  const updateOverdueFollowUps = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Call edge function to reschedule any overdue follow-ups
      await supabase.functions.invoke('update-overdue-followups', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });
    } catch (error) {
      console.error('Error updating overdue follow-ups:', error);
    } finally {
      // Load follow-ups after updating
      loadFollowUps();
    }
  };

  const loadFollowUps = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('d2d_units')
        .select(`
          id,
          next_reminder,
          unit_name,
          customer_first_name,
          customer_last_name,
          d2d_houses!inner (
            id,
            house_number,
            d2d_streets!inner (
              id,
              name,
              d2d_areas!inner (
                id,
                name
              )
            )
          )
        `)
        .eq('user_id', userId)
        .not('next_reminder', 'is', null)
        .contains('tags', ['KT'])
        .order('next_reminder', { ascending: true });

      if (error) throw error;
      setFollowUps(data || []);
    } catch (error) {
      console.error('Error loading follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedDateFollowUps = followUps.filter(fu => 
    selectedDate && fu.next_reminder && isSameDay(new Date(fu.next_reminder), selectedDate)
  );

  const datesWithFollowUps = followUps
    .filter(fu => fu.next_reminder)
    .map(fu => new Date(fu.next_reminder));

  const handleFollowUpClick = async (followUp: FollowUp) => {
    // Close the calendar
    onOpenChange(false);
    
    // Navigate to running list with the unit selected
    navigate('/running-list', {
      state: {
        selectedUnitId: followUp.id,
        selectedAreaId: followUp.d2d_houses.d2d_streets.d2d_areas.id,
        selectedStreetId: followUp.d2d_houses.d2d_streets.id,
        selectedHouseId: followUp.d2d_houses.id
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Follow-Up Kalender</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
          <div className="flex flex-col gap-6 pb-4">
          {/* Calendar */}
          <div className="flex flex-col items-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              locale={de}
              modifiers={{
                hasFollowUp: datesWithFollowUps
              }}
              modifiersClassNames={{
                hasFollowUp: "bg-primary/20 font-bold"
              }}
              className="rounded-md border"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Tage mit Follow-ups sind hervorgehoben
            </p>
          </div>

          {/* Follow-ups for selected date */}
          <div className="flex flex-col">
            <h3 className="font-medium mb-3">
              {selectedDate ? format(selectedDate, "d. MMMM yyyy", { locale: de }) : "Datum wählen"}
            </h3>
            
            <div className="space-y-3">
              {loading ? (
                <p className="text-sm text-muted-foreground">Lade...</p>
              ) : selectedDateFollowUps.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Keine Follow-ups für diesen Tag geplant
                </p>
              ) : (
                <>
                  {selectedDateFollowUps
                    .sort((a, b) => 
                      new Date(a.next_reminder).getTime() - new Date(b.next_reminder).getTime()
                    )
                    .map(followUp => (
                      <div 
                        key={followUp.id}
                        onClick={() => handleFollowUpClick(followUp)}
                        className="p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-primary" />
                          <span className="font-semibold text-base">
                            {format(new Date(followUp.next_reminder), "HH:mm")} Uhr
                          </span>
                        </div>
                        
                        {(followUp.customer_first_name || followUp.customer_last_name) && (
                          <div className="font-medium text-base mb-2 ml-6">
                            {followUp.customer_first_name} {followUp.customer_last_name}
                          </div>
                        )}
                        
                        <div className="flex items-start gap-2 text-sm text-muted-foreground ml-6">
                          <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                          <div className="text-xs">
                            <div>{followUp.d2d_houses.d2d_streets.d2d_areas.name}</div>
                            <div>
                              {followUp.d2d_houses.d2d_streets.name} {followUp.d2d_houses.house_number}
                              {followUp.unit_name && ` - ${followUp.unit_name}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </>
              )}
            </div>
          </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
